import { Picture } from "@/components/media/Picture";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { ContactForm } from "@/components/forms/ContactForm";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import { site } from "@/data/site";
import { pageBlocks } from "@/lib/content";

const TITLE = "Contact Us — Light of Life International";
const DESCRIPTION =
  "Reach the Light of Life International team in Fort Worth, Texas by phone, email or the contact form.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/contact" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: site.name,
          email: site.email,
          telephone: site.phone,
          address: { "@type": "PostalAddress", streetAddress: site.address },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-9 w-9" aria-hidden>
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-9 w-9" aria-hidden>
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24c1.1.37 2.3.57 3.5.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.2.2 2.4.57 3.5a1 1 0 0 1-.25 1l-2.22 2.3Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-9 w-9" aria-hidden>
      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm8 7.2L4.4 6.5v.2L12 12l7.6-5.3v-.2L12 11.2Z" />
    </svg>
  );
}

function ContactPage() {
  const blocks = pageBlocks("contact");
  const text = (i: number) => {
    const block = blocks[i];
    return block && "text" in block ? block.text : "";
  };

  const fullVerse = text(1);
  const match = fullVerse.match(/^(.*?)\s*(Acts\s[\d:]+)$/);
  const verse = match ? match[1] : fullVerse;
  const reference = match ? match[2] : "";

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      <main id="main" className="flex-1">
        <PageHero title={text(0) || "Contact"} image={heroFor("/contact").image} />

        {/* Verse + contact cards */}
        <section className="relative overflow-hidden bg-background py-20">
          <img
            src="/media/imageye___-_imgi_8_backgroundpattern-tr.png"
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.06]"
          />
          <div className="container-site relative">
            <h2 className="mx-auto max-w-4xl text-center text-4xl normal-case leading-tight text-foreground sm:text-5xl">
              {verse}
            </h2>
            <p className="mt-4 text-center font-heading text-3xl normal-case text-foreground sm:text-4xl">
              {reference}
            </p>
            <p className="mt-2 text-right font-heading text-xl normal-case italic text-accent">
              {text(2)}
            </p>

            <div className="mt-16 grid gap-6 md:grid-cols-3">
              <div className="rounded-lg bg-muted px-8 py-10 text-center">
                <div className="flex justify-center text-muted-foreground/50">
                  <PinIcon />
                </div>
                <h3 className="mt-4 text-2xl text-navy">{text(3) || "LOLI Address"}</h3>
                <p className="mt-4 font-sans text-sm uppercase tracking-wider text-foreground">
                  <a href={site.addressUrl} target="_blank" rel="noreferrer">
                    {site.address}
                  </a>
                </p>
              </div>
              <div className="rounded-lg bg-muted px-8 py-10 text-center">
                <div className="flex justify-center text-muted-foreground/50">
                  <PhoneIcon />
                </div>
                <h3 className="mt-4 text-2xl text-navy">{text(4) || "Phone Number"}</h3>
                <p className="mt-4 font-sans text-sm uppercase tracking-wider">
                  <a href={site.phoneHref} className="text-accent-foreground">
                    {site.phone}
                  </a>
                </p>
              </div>
              <div className="rounded-lg bg-muted px-8 py-10 text-center">
                <div className="flex justify-center text-muted-foreground/50">
                  <MailIcon />
                </div>
                <h3 className="mt-4 text-2xl text-navy">{text(5) || "Email"}</h3>
                <p className="mt-4 font-sans text-sm uppercase tracking-wider">
                  <a href="mailto:info@loli.org">info@loli.org</a>
                  <span aria-hidden> | </span>
                  <a href="mailto:prayer@loli.org">prayer@loli.org</a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Navy invitation band */}
        <section className="relative overflow-hidden bg-navy py-24">
          <Picture
            src="/media/vol_07-1.jpg"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-15"
            sizes="100vw"
          />
          <p className="container-site relative mx-auto max-w-4xl text-center font-heading text-2xl leading-snug text-white sm:text-3xl">
            {text(8)}
          </p>
        </section>

        {/* Get in touch form + photo */}
        <section className="grid bg-navy lg:grid-cols-2">
          <div className="px-6 py-20 sm:px-12 lg:pr-16 lg:pl-[max(3rem,calc((100vw-80rem)/2+1.5rem))]">
            <h2 className="text-4xl text-white sm:text-5xl">
              Get In <span className="text-accent">Touch!</span>
            </h2>
            <div className="mt-10">
              <ContactForm />
            </div>
          </div>
          <Picture
            src="/media/vol_01-1.jpg"
            alt="Light of Life International mission team with children on an outreach"
            loading="lazy"
            decoding="async"
            className="h-full min-h-[24rem] w-full object-cover"
            sizes="(max-width: 768px) 100vw, 50vw"
            widths={[640, 1024]}
          />
        </section>

        {/* Map (click-to-load so Google sets no cookies on page load) */}
        <OfficeMap />
      </main>

      <Footer />
    </div>
  );
}

function OfficeMap() {
  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <iframe
        title="Light of Life International office location"
        src="https://www.google.com/maps?q=4364+Western+Center+Blvd+%23124+Fort+Worth+TX+76137&output=embed"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        className="h-[420px] w-full border-0"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setLoaded(true)}
      className="flex h-[420px] w-full flex-col items-center justify-center gap-3 bg-muted text-center"
    >
      <span className="font-sans text-lg font-semibold text-foreground">
        4364 Western Center Blvd #124, Fort Worth, TX 76137
      </span>
      <span className="rounded-md bg-navy px-6 py-3 font-sans font-semibold text-white">
        Load map
      </span>
    </button>
  );
}
